import type { ContentfulEntry, ContentfulRichTextNode } from '@/types/contentful'

const CONTENTFUL_SPACE_ID = import.meta.env.VITE_CONTENTFUL_SPACE_ID || ''
const CONTENTFUL_ENVIRONMENT = import.meta.env.VITE_CONTENTFUL_ENVIRONMENT || 'master'
const CONTENTFUL_DELIVERY_TOKEN = import.meta.env.VITE_CONTENTFUL_DELIVERY_TOKEN || ''
const WHATS_NEW_CONTENT_TYPE = import.meta.env.VITE_CONTENTFUL_WHATS_NEW_CONTENT_TYPE || 'whatsNew'
const RELEASE_NOTES_CONTENT_TYPE = import.meta.env.VITE_CONTENTFUL_RELEASE_NOTES_CONTENT_TYPE || 'releaseNote'

interface ContentfulResponse {
  items?: Array<{
    sys?: { id?: string; createdAt?: string; publishedAt?: string; firstPublishedAt?: string }
    fields?: Record<string, unknown>
  }>
  includes?: {
    Asset?: Array<{
      sys?: { id?: string }
      fields?: {
        file?: {
          url?: string
        }
      }
    }>
  }
}

export function isContentfulConfigured(): boolean {
  return Boolean(CONTENTFUL_SPACE_ID && CONTENTFUL_DELIVERY_TOKEN)
}

function toStringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function extractLocalizedField<T>(value: unknown): T | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value as T | undefined
  }
  const localizedRecord = value as Record<string, unknown>
  // Rich Text/document objects come as direct objects, not locale maps.
  if ('nodeType' in localizedRecord || 'content' in localizedRecord) {
    return value as T
  }
  const localeKeys = Object.keys(localizedRecord)
  if (localeKeys.length === 0) {
    return undefined
  }
  const looksLocalized = localeKeys.some((key) => /^[a-z]{2}-[A-Z]{2}$/.test(key))
  if (!looksLocalized) {
    return value as T
  }
  const preferred =
    localizedRecord['sv-SE'] ??
    localizedRecord['en-US'] ??
    localizedRecord[localeKeys[0]]
  return preferred as T | undefined
}

function richTextToPlainText(node: ContentfulRichTextNode | undefined): string {
  if (!node) {
    return ''
  }

  if (node.nodeType === 'text') {
    return node.value ?? ''
  }

  const content = node.content ?? []
  const childText = content.map((child) => richTextToPlainText(child)).join('')

  if (
    node.nodeType === 'paragraph' ||
    node.nodeType === 'heading-1' ||
    node.nodeType === 'heading-2' ||
    node.nodeType === 'heading-3' ||
    node.nodeType === 'heading-4' ||
    node.nodeType === 'heading-5' ||
    node.nodeType === 'heading-6' ||
    node.nodeType === 'blockquote' ||
    node.nodeType === 'unordered-list' ||
    node.nodeType === 'ordered-list' ||
    node.nodeType === 'list-item'
  ) {
    return `${childText}\n`
  }

  return childText
}

function resolveAssetUrl(
  fields: Record<string, unknown>,
  response: ContentfulResponse
): string | undefined {
  const imageField = fields.image as
    | { sys?: { id?: string } }
    | { fields?: { file?: { url?: string } } }
    | undefined

  const directUrl = imageField?.fields?.file?.url
  if (typeof directUrl === 'string' && directUrl.length > 0) {
    return directUrl.startsWith('//') ? `https:${directUrl}` : directUrl
  }

  const assetId = imageField?.sys?.id
  if (!assetId) {
    return undefined
  }

  const asset = response.includes?.Asset?.find((candidate) => candidate.sys?.id === assetId)
  const assetUrl = asset?.fields?.file?.url
  if (!assetUrl) {
    return undefined
  }
  return assetUrl.startsWith('//') ? `https:${assetUrl}` : assetUrl
}

function mapEntry(
  item: NonNullable<ContentfulResponse['items']>[number],
  response: ContentfulResponse
): ContentfulEntry {
  const fields = item.fields ?? {}
  const titleField = extractLocalizedField<string>(fields.title)
  const descriptionField = extractLocalizedField<ContentfulRichTextNode | string>(fields.description)
  const releaseDateField = extractLocalizedField<string>(fields.releaseDate)
  const publishedAtFieldValue = extractLocalizedField<string>(fields.publishedAt)
  const dateField = extractLocalizedField<string>(fields.date)
  const urlField = extractLocalizedField<string>(fields.url)
  const linkField = extractLocalizedField<string>(fields.link)
  const description =
    typeof descriptionField === 'string'
      ? descriptionField
      : richTextToPlainText(descriptionField).trim()

  const publishedAtField =
    toStringValue(releaseDateField) ||
    toStringValue(publishedAtFieldValue) ||
    toStringValue(dateField)
  const createdAt = item.sys?.createdAt || new Date().toISOString()
  const publishedAt = item.sys?.firstPublishedAt || item.sys?.publishedAt

  return {
    id: item.sys?.id || crypto.randomUUID(),
    title: toStringValue(titleField) || 'Untitled',
    description,
    richDescription: typeof descriptionField === 'string' ? undefined : descriptionField,
    publishedAt: publishedAtField || publishedAt || createdAt,
    url: toStringValue(urlField) || toStringValue(linkField) || undefined,
    imageUrl: resolveAssetUrl(fields, response),
  }
}

export async function getContentfulEntries(contentType: string, limit = 5): Promise<ContentfulEntry[]> {
  if (!isContentfulConfigured()) {
    return []
  }

  const params = new URLSearchParams({
    content_type: contentType,
    order: '-fields.releaseDate,-sys.publishedAt',
    limit: String(limit),
    include: '2',
  })

  const url = `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}/entries?${params.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${CONTENTFUL_DELIVERY_TOKEN}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Contentful request failed (${response.status})`)
  }

  const data = (await response.json()) as ContentfulResponse
  return (data.items ?? []).map((item) => mapEntry(item, data))
}

export async function getWhatsNewEntries(): Promise<ContentfulEntry[]> {
  return getContentfulEntries(WHATS_NEW_CONTENT_TYPE, 5)
}

export async function getReleaseNotesEntries(): Promise<ContentfulEntry[]> {
  return getContentfulEntries(RELEASE_NOTES_CONTENT_TYPE, 8)
}

export async function getContentfulEntriesByEntryType(
  entryType: 'whats-new' | 'release-note',
  limit = 100
): Promise<ContentfulEntry[]> {
  const contentType =
    entryType === 'release-note' ? RELEASE_NOTES_CONTENT_TYPE : WHATS_NEW_CONTENT_TYPE
  return getContentfulEntries(contentType, limit)
}

export async function getContentfulEntryById(
  entryId: string,
  entryType: 'whats-new' | 'release-note'
): Promise<ContentfulEntry> {
  const entries = await getContentfulEntriesByEntryType(entryType, 100)
  const entry = entries.find((candidate) => candidate.id === entryId)

  if (!entry) {
    throw new Error('Entry not found')
  }

  return entry
}
